// ─── "מאז רכישה" in shekels, at the rate of the purchase day (sprint 13, 5.2) ─
//
// The gain since purchase in shekels used to be the dollar gain times TODAY's
// rate · "לפי שער היום". That mixes two things: the stock's move and the
// dollar's move since the purchase. With the Bank of Israel representative
// rate of the purchase day, the cost is what it was in shekels then, and the
// value is what it is in shekels now.
//
// Source: Bank of Israel, series RER_USD_ILS, through the marketdata function
// (op=boiHistory). A date without a publication takes the last rate before
// it, and the date of that rate travels with it. A settled rate does not
// change, so it is kept on this device (not a same-day one, see below).
//
// When a holding has no purchase date, or its date has no rate, or the rates
// did not load, there is no figure: the screen shows "—" and says why
// (bug round, 17/10/2026, David: the purchase-day method only). The old rule,
// the dollar gain at today's rate, used to stand in until the rates arrived,
// so the same card showed two different shekel sums a second apart.
// Nothing is estimated.
import { useEffect, useState } from "react";

const KEY = "si_boi_rer_v1";
const readCache = () => { try { return JSON.parse(localStorage.getItem(KEY) || "{}") || {}; } catch { return {}; } };
const writeCache = m => { try { localStorage.setItem(KEY, JSON.stringify(m)); } catch {} };

const isoDay = d => (typeof d === "string" && /^\d{4}-\d{2}-\d{2}/.test(d) ? d.slice(0, 10) : null);

// { rates, loading, failed } for every purchase date in the portfolio:
//   rates   · { "2025-01-04": { rate, rateDate } }
//   loading · a date with no rate yet is still being asked for
//   failed  · the request for the missing dates failed
export function usePurchaseRates(portfolio = []) {
  const dates = [...new Set(portfolio.map(p => isoDay(p.buyDate)).filter(Boolean))].sort();
  const key = dates.join(",");
  const [rates, setRates] = useState(() => {
    const c = readCache();
    return Object.fromEntries(dates.filter(d => c[d]).map(d => [d, c[d]]));
  });
  // The dates already asked for in this session (answered or not), and
  // whether that request failed. A date not asked for yet is loading.
  const [asked, setAsked] = useState(() => new Set());
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!key) return undefined;
    const cache = readCache();
    const missing = key.split(",").filter(d => !cache[d]);
    const known = Object.fromEntries(key.split(",").filter(d => cache[d]).map(d => [d, cache[d]]));
    setRates(prev => ({ ...prev, ...known }));
    if (!missing.length) return undefined;
    let alive = true;
    const done = ok => {
      if (!alive) return;
      setAsked(prev => { const n = new Set(prev); missing.forEach(d => n.add(d)); return n; });
      setFailed(!ok);
    };
    fetch(`/.netlify/functions/marketdata?op=boiHistory&dates=${missing.join(",")}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!alive) return;
        if (!d?.rates) { done(false); return; }
        const got = Object.fromEntries(Object.entries(d.rates).filter(([, v]) => v && v.rate > 0));
        // Kept for good only when final: the rate of that very day, or a date
        // old enough that no later publication can apply to it. A purchase
        // made today, before the BOI publishes, shows yesterday's rate now and
        // is asked again next time.
        const final = Object.fromEntries(Object.entries(got).filter(([date, v]) =>
          v.rateDate === date || Date.now() - Date.parse(date) > 5 * 86400000));
        writeCache({ ...readCache(), ...final });
        setRates(prev => ({ ...prev, ...got }));
        done(true);
      })
      .catch(() => done(false));
    return () => { alive = false; };
  }, [key]);
  const loading = dates.some(d => !rates[d] && !asked.has(d));
  return { rates, loading, failed };
}

// The gain since purchase in shekels for a set of holdings, or null when any
// of them lacks a purchase-day rate (the caller then falls back to today's
// rate and says so). `todayRate` is the live USD/ILS rate.
//   { ils, pct, costIls, rateDates: [...] }
export function sinceInShekels(holdings, rates, todayRate) {
  if (!(todayRate > 0)) return null;
  const rows = holdings.filter(p => p.quote !== "none" && p.currentPrice > 0 && p.buyPrice > 0 && p.shares > 0);
  if (!rows.length) return null;
  let cost = 0, value = 0;
  const rateDates = [];
  for (const p of rows) {
    const r = rates[isoDay(p.buyDate)];
    if (!r || !(r.rate > 0)) return null;
    cost += p.buyPrice * p.shares * r.rate;
    value += p.currentPrice * p.shares * todayRate;
    rateDates.push(r.rateDate);
  }
  return { ils: value - cost, pct: cost > 0 ? ((value - cost) / cost) * 100 : 0, costIls: cost, rateDates };
}

// The gain since purchase in shekels as the screen states it, one of:
//   { state: "ready", ils, pct, costIls, rateDates }
//   { state: "loading" }        · a purchase-day rate is still on its way
//   { state: "none", why }      · no figure, and the sentence that says why
//   null                        · nothing to measure (no buy price, no quote)
// `pr` is what usePurchaseRates returned; `todayRate` the live USD/ILS rate.
export function shekelSince(holdings, pr, todayRate) {
  const rows = holdings.filter(p => p.quote !== "none" && p.currentPrice > 0 && p.buyPrice > 0 && p.shares > 0);
  if (!rows.length) return null;
  if (!(todayRate > 0)) return { state: "none", why: "אין שער דולר חי כרגע" };
  const r = sinceInShekels(rows, pr.rates, todayRate);
  if (r) return { state: "ready", ...r };
  const noDate = rows.find(p => !isoDay(p.buyDate));
  if (noDate) return { state: "none", why: `להחזקה ${noDate.symbol} אין תאריך רכישה, ולכן אין שער יציג ליום הרכישה` };
  if (pr.loading) return { state: "loading" };
  if (pr.failed) return { state: "none", why: "השער היציג של בנק ישראל ליום הרכישה לא נטען · נסה לרענן" };
  const noRate = rows.find(p => !(pr.rates[isoDay(p.buyDate)]?.rate > 0));
  return { state: "none", why: `אין שער יציג של בנק ישראל ליום הרכישה${noRate ? ` של ${noRate.symbol}` : ""}` };
}

// Under every shekel sum since purchase: the sum moves with the dollar too.
export const FX_EFFECT_LABEL = "כולל השפעת שער הדולר";

export const PURCHASE_RATE_LABEL = "לפי השער היציג ביום הרכישה";
export const TODAY_RATE_LABEL = "לפי שער היום";
// Sprint 13, 5.3 · the gain counts the price only. A dividend paid in the
// meantime is not in it, and the label says so.
export const PRICE_ONLY_LABEL = "לפי מחיר בלבד, בלי דיבידנדים";
